// Slide-to-confirm — drag the thumb from the left edge to the right edge
// to trigger `onConfirm`. Used for stamp + redeem buttons where a
// mis-click at the counter would write a row to the DB; the slide
// gesture is a deliberate 2-step interaction.
//
// Threshold = 75% of the track width — drop short and the thumb springs
// back. Commits fire a short navigator.vibrate(15) where supported.
//
// Perf: drag is driven by direct DOM writes via refs (no React
// re-render per pointermove). Fill animates via GPU `transform: scaleX`
// rather than `width %` so the drag doesn't trigger layout/paint.
//
// `pending`: parent-controlled "request in flight" state. While true the
// thumb is locked to the right, the label shows confirmingLabel, and a
// spinner replaces the arrow. The parent flips it back to false once the
// server has confirmed (and typically dismisses the surrounding modal at
// the same time, so the reset isn't user-visible).

import { useCallback, useEffect, useRef } from 'react'
import './slideToConfirm.css'
import { unlockStampSound } from './sound/stampSound.js'

interface Props {
  label: string
  confirmingLabel?: string
  onConfirm: () => void
  disabled?: boolean
  pending?: boolean
  /** Visual variant. 'primary' = brand color, 'success' = green, 'danger' = red. */
  tone?: 'primary' | 'success' | 'danger'
}

const COMMIT_THRESHOLD = 0.75

export function SlideToConfirm({
  label,
  confirmingLabel,
  onConfirm,
  disabled = false,
  pending = false,
  tone = 'primary',
}: Props) {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const thumbRef = useRef<HTMLDivElement | null>(null)
  const fillRef = useRef<HTMLDivElement | null>(null)
  const offsetRef = useRef(0)
  const dragStartXRef = useRef<number | null>(null)
  const maxOffsetRef = useRef(0)

  const measure = useCallback(() => {
    const track = trackRef.current
    if (!track) return 0
    // Thumb is 56px wide via CSS; the maximum offset is track width minus
    // thumb width minus 4px padding on each side.
    return Math.max(0, track.clientWidth - 56 - 8)
  }, [])

  // Single helper so every transform write — drag, commit, reset, pending
  // lock, keyboard activation — goes through the same path and can't drift.
  const setProgress = useCallback((px: number) => {
    offsetRef.current = px
    const max = maxOffsetRef.current
    const scale = max > 0 ? px / max : 0
    if (thumbRef.current) {
      thumbRef.current.style.transform = `translateX(${px}px)`
    }
    if (fillRef.current) {
      fillRef.current.style.transform = `scaleX(${scale})`
    }
  }, [])

  useEffect(() => {
    maxOffsetRef.current = measure()
    const onResize = () => {
      maxOffsetRef.current = measure()
      // Keep the thumb pinned to the right edge if we're locked-pending
      // while the window resizes.
      if (pending) setProgress(maxOffsetRef.current)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [measure, pending, setProgress])

  // While the parent reports `pending`, lock the thumb to the right edge.
  // When the parent clears `pending` (mutation done — usually accompanied
  // by an unmount of the surrounding modal), reset to the left edge.
  useEffect(() => {
    if (pending) {
      setProgress(maxOffsetRef.current)
    } else {
      setProgress(0)
    }
  }, [pending, setProgress])

  const inactive = disabled || pending

  const reset = useCallback(() => {
    if (pending) return
    trackRef.current?.classList.remove('is-dragging')
    setProgress(0)
    dragStartXRef.current = null
  }, [pending, setProgress])

  const onPointerDown = (e: React.PointerEvent) => {
    // Prime the stamp-success <audio> element synchronously inside this
    // gesture — see stampSound.ts for why iOS WebKit requires this.
    unlockStampSound()
    if (inactive) return
    dragStartXRef.current = e.clientX - offsetRef.current
    maxOffsetRef.current = measure()
    ;(e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId)
    trackRef.current?.classList.add('is-dragging')
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (dragStartXRef.current == null || inactive) return
    const next = Math.max(
      0,
      Math.min(maxOffsetRef.current, e.clientX - dragStartXRef.current),
    )
    setProgress(next)
  }

  const onPointerUp = (e: React.PointerEvent) => {
    if (dragStartXRef.current == null) return
    ;(e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId)
    trackRef.current?.classList.remove('is-dragging')
    const max = maxOffsetRef.current
    const threshold = max * COMMIT_THRESHOLD
    if (offsetRef.current >= threshold) {
      setProgress(max)
      dragStartXRef.current = null
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        navigator.vibrate(15)
      }
      onConfirm()
    } else {
      reset()
    }
  }

  return (
    <div
      ref={trackRef}
      className={[
        'slide-confirm',
        `slide-confirm-${tone}`,
        disabled ? 'is-disabled' : '',
        pending ? 'is-pending' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={inactive ? -1 : 0}
      aria-disabled={inactive}
      aria-busy={pending}
      aria-label={label}
      onKeyDown={(e) => {
        if (inactive) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setProgress(maxOffsetRef.current)
          if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            navigator.vibrate(15)
          }
          onConfirm()
        }
      }}
    >
      <div ref={fillRef} className="slide-confirm-fill" aria-hidden="true" />
      <span className="slide-confirm-label">
        {pending ? confirmingLabel || label : label}
      </span>
      <div
        ref={thumbRef}
        className="slide-confirm-thumb"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={reset}
        aria-hidden="true"
      >
        {pending ? (
          <span className="slide-confirm-spinner" />
        ) : (
          <span className="slide-confirm-arrow">→</span>
        )}
      </div>
    </div>
  )
}
